import type { Locale } from "date-fns";
import { da, de, enUS, es, fr, it, ja, nl, ptBR, zhCN } from "date-fns/locale";

import { DEFAULT_LANGUAGE, normalizeLanguage } from "./languages";

/**
 * date-fns locale objects for the languages we ship.
 *
 * `Intl` covers plain dates and numbers, but date-fns owns two things Intl
 * does not: token patterns (`format(d, "MMM d, yyyy")`, used 88× here) and the
 * calendar/date-picker components, which need month and weekday names in the
 * user's language. Those calls take a `{ locale }` option — without it date-fns
 * silently formats in English regardless of the UI language, which is exactly
 * the state this file ends.
 *
 * Regional pairings match `SUPPORTED_LANGUAGES.intlTag`: pt → pt-BR, zh → zh-CN.
 */
export const DATE_FNS_LOCALES: Record<string, Locale> = {
  da,
  de,
  en: enUS,
  es,
  fr,
  it,
  ja,
  nl,
  pt: ptBR,
  zh: zhCN,
};

/**
 * date-fns locale for a language code or tag, falling back to English.
 *
 * Tolerates a full tag ("pt-BR", "zh-Hans-CN") the same way `normalizeLanguage`
 * does, so a caller can hand it `i18n.language` without thinking about shape.
 */
export function dateFnsLocaleFor(code: string | null | undefined): Locale {
  const normalized = normalizeLanguage(code) ?? DEFAULT_LANGUAGE;
  return DATE_FNS_LOCALES[normalized] ?? DATE_FNS_LOCALES[DEFAULT_LANGUAGE];
}
